import { gql, useMutation } from "@apollo/client";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { EditBookingInput } from "../../../__generated__/graphql";
import { useNotifications } from "../../../components/Notification";
import styles from "./Form.module.scss";

type BookingFormProps = {
  bookingId: string;
  disabled: boolean;
  email: string;
  phoneNumber?: string | null;
  specialRequests?: string | null;
};

export default function BookingForm({
  bookingId,
  disabled,
  email,
  phoneNumber,
  specialRequests,
}: BookingFormProps) {
  const { showNotification } = useNotifications();
  const [values, setValues] = useState({
    email,
    phoneNumber: phoneNumber || "",
    specialRequests: specialRequests || "",
  });
  const [editBooking, { loading }] = useMutation(EDIT_BOOKING);

  useEffect(() => {
    setValues({
      email,
      phoneNumber: phoneNumber || "",
      specialRequests: specialRequests || "",
    });
  }, [email, phoneNumber, specialRequests]);

  const hasChanges =
    values.email !== email ||
    values.phoneNumber !== (phoneNumber || "") ||
    values.specialRequests !== (specialRequests || "");

  const setValue = (field: keyof typeof values, value: string) =>
    setValues((current) => ({ ...current, [field]: value }));

  async function handleSave() {
    const input: EditBookingInput = { bookingId };

    if (values.email !== email) input.email = values.email;
    if (values.phoneNumber !== (phoneNumber || ""))
      input.phoneNumber = values.phoneNumber;
    if (values.specialRequests !== (specialRequests || ""))
      input.specialRequests = values.specialRequests;

    try {
      await editBooking({
        variables: { input },
        refetchQueries: ["GetBookings"],
      });
      showNotification({
        severity: "success",
        message: "Booking details have been updated",
      });
    } catch (error) {
      showNotification({
        severity: "error",
        message:
          error instanceof Error ? error.message : "Unable to save booking",
      });
    }
  }

  return (
    <Box className={styles.form}>
      <BookingField
        label="Email address"
        value={values.email}
        type="email"
        disabled={disabled}
        onChange={(value) => setValue("email", value)}
      />
      <BookingField
        label="Phone number"
        value={values.phoneNumber}
        disabled={disabled}
        onChange={(value) => setValue("phoneNumber", value)}
      />
      <BookingField
        label="Special requests"
        value={values.specialRequests}
        disabled={disabled}
        onChange={(value) => setValue("specialRequests", value)}
        fullWidth
        multiline
      />
      {hasChanges && !disabled && (
        <Box className={styles.actions}>
          <Button
            variant="contained"
            size="small"
            className={styles.saveButton}
            disabled={loading || !values.email.trim()}
            onClick={handleSave}
          >
            {loading ? <CircularProgress color="inherit" size={20} /> : "Save"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function BookingField(props: {
  label: string;
  value: string;
  type?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  multiline?: boolean;
}) {
  return (
    <Box className={props.fullWidth ? styles.fullWidthField : styles.field}>
      <TextField
        fullWidth
        label={props.label}
        value={props.value}
        type={props.type}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.value)}
        size="small"
        variant="outlined"
        multiline={props.multiline}
        minRows={props.multiline ? 2 : undefined}
        InputLabelProps={{ shrink: true }}
        className={styles.textField}
      />
    </Box>
  );
}

const EDIT_BOOKING = gql(`
  mutation EditBooking($input: EditBookingInput!) {
    editBooking(input: $input)
  }
`);
